import { ButtonFlavor, ButtonSize, ButtonType, ExButton, ExDialog } from '@boomi/exosphere';
import { useRouting } from '../../../hooks/route';
import translations from '../../../translations';
import type { DebugAction } from '../../../types/debugger';
import type { Tab } from '../../../types/tab';
import { stringReplace } from '../../../utils/string';
import { useTabs } from '../../tabs/TabsProvider';
import { useDebugDispatch, useDebugState } from './DebugProvider';

const confirmSwitchFlow = (
    dispatch: (action: DebugAction) => void,
    switchFlow: ReturnType<typeof useRouting>['switchFlow'],
    tabs: Tab[],
    activeTabKey: string | null,
    flowId: string,
    stackItemFlowId: string,
    stackItemFlowName: string,
) => {
    const tab = tabs.find((tab) => tab.key === activeTabKey);
    if (tab && stackItemFlowId !== flowId) {
        switchFlow(tab.key, stackItemFlowId, stackItemFlowName);
    }
    dispatch({ type: 'dismissSwitchFlowConfirmation' });
};

interface Props {
    flowId: string;
}

const SwitchFlowConfirmation = ({ flowId }: Props) => {
    const { switchFlowConfirmation } = useDebugState();
    const dispatch = useDebugDispatch();
    const { tabs, activeTabKey } = useTabs();
    const { switchFlow } = useRouting();

    return (
        <ExDialog
            open={switchFlowConfirmation !== null}
            dialogTitle={translations.DEBUGGER_confirmation_title}
            hideClose
        >
            {switchFlowConfirmation?.canSwitch
                ? stringReplace(translations.DEBUGGER_confirmation_summary, {
                      flowName: switchFlowConfirmation?.stackItemFlowName,
                  })
                : stringReplace(translations.DEBUGGER_confirmation_summary_tab_conflict, {
                      flowName: switchFlowConfirmation?.stackItemFlowName,
                  })}
            {switchFlowConfirmation?.canSwitch && (
                <ExButton
                    slot="footer"
                    flavor={ButtonFlavor.BRANDED}
                    type={ButtonType.SECONDARY}
                    size={ButtonSize.LARGE}
                    onClick={() => {
                        if (switchFlowConfirmation) {
                            confirmSwitchFlow(
                                dispatch,
                                switchFlow,
                                tabs,
                                activeTabKey,
                                flowId,
                                switchFlowConfirmation.stackItemFlowId,
                                switchFlowConfirmation.stackItemFlowName,
                            );
                        }
                    }}
                >
                    {translations.COMMON_confirm}
                </ExButton>
            )}
            <ExButton
                slot="footer"
                flavor={ButtonFlavor.BRANDED}
                type={ButtonType.SECONDARY}
                size={ButtonSize.LARGE}
                onClick={() => {
                    if (switchFlowConfirmation?.stackItemId) {
                        dispatch({
                            type: 'dismissSwitchFlowConfirmation',
                        });
                    }
                }}
            >
                {switchFlowConfirmation?.canSwitch
                    ? translations.COMMON_cancel
                    : translations.COMMON_ok}
            </ExButton>
        </ExDialog>
    );
};

export default SwitchFlowConfirmation;
